/**
 * AppDialog — the ONE shared New/Edit popup for every form (standing rule 12).
 *
 * Faithful to the MDL Study `.modal` standard: 460px default (right-sized — set
 * `width` to hug a form's controls), grey `#f0f0f0` header, 6px radius,
 * `rgba(0,0,0,.45)` backdrop, z-index 1500, body scrolls internally past 92vh.
 *
 * Forms pass only their title + body + footer; they NEVER style the overlay,
 * header, backdrop or sizing themselves.  Retune appDialog.css once and every
 * popup in the app — current and future — changes together.
 *
 * @example
 * <AppDialog open={open} title="New Method" icon="ti-flask" onClose={close}
 *   footer={<><button className="ca-btn" onClick={close}>Cancel</button>
 *            <button className="ca-btn ca-btn-solid" onClick={save}>Save</button></>}>
 *   ...form fields...
 * </AppDialog>
 */
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './appDialog.css';

export interface AppDialogProps {
  /** Whether the dialog is shown.  When false the component renders nothing. */
  open: boolean;
  /** Header title text (left of the close ✕). */
  title: ReactNode;
  /** Optional Tabler icon class for the header (e.g. "ti-flask"). */
  icon?: string;
  /** Body content — the form fields.  Hugs its content; scrolls past 92vh. */
  children: ReactNode;
  /** Footer content — typically the Cancel / Save buttons. Omit for none. */
  footer?: ReactNode;
  /** Dialog width in px (default 460 — size it to FIT the controls). */
  width?: number;
  /** Close when the backdrop is clicked (default true). */
  closeOnBackdrop?: boolean;
  /** Close when Escape is pressed (default true). */
  closeOnEsc?: boolean;
  /** Called when the user closes via ✕, backdrop, or Escape. */
  onClose: () => void;
  /** Extra class on the OVERLAY, for a form that themes its own popup
   *  (e.g. Sample Registration's accent header via `.sr-dlg`).  Use this
   *  instead of wrapping `<AppDialog>` in a styling div — a wrapper would sit
   *  in the page's layout flow and, under `.content > * { flex: 1 }`, steal
   *  half the page height from the list behind it. */
  className?: string;
  /** Show a Maximize / Restore button (before the close ✕) that toggles the modal
   *  to near-full-screen — GoLive/XAF popup window chrome. Opt-in per dialog. */
  maximizable?: boolean;
}

export function AppDialog({
  open, title, icon, children, footer,
  width = 460, closeOnBackdrop = true, closeOnEsc = true, onClose, className,
  maximizable = false,
}: AppDialogProps) {
  const [max, setMax] = useState(false);

  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEsc, onClose]);

  // Always reopen at normal size.
  useEffect(() => { if (!open) setMax(false); }, [open]);

  if (!open) return null;

  // PORTALED to <body>, never rendered inline in the page (fix 2026-07-29).
  // A page returns `<>{page}{dialog}</>`, so an inline dialog becomes a SIBLING
  // of the page inside the shell's `.content` — and `.content > * { flex: 1 }`
  // (shell.css) then splits the height EQUALLY between them.  The visible bug:
  // opening Sample Registration's "Registration Options" collapsed the list
  // behind it from ~16 rows to 4.  A portal takes the dialog out of every
  // page's layout flow, so no popup can ever resize the form behind it.
  return createPortal(
    <div
      className={`ad-overlay${className ? ` ${className}` : ''}${max ? ' ad-overlay-max' : ''}`}
      onMouseDown={(e) => { if (closeOnBackdrop && e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`ad-modal${max ? ' ad-maximized' : ''}`}
        style={max ? { width: '96vw', height: '92vh', maxHeight: '92vh' } : { width }}
        role="dialog"
        aria-modal="true"
      >
        <div className="ad-hdr">
          <span className="ad-title">
            {icon && <i className={`ti ${icon}`} aria-hidden="true" />} {title}
          </span>
          <span className="ad-hdr-btns">
            {maximizable && (
              <button className="ad-max" onClick={() => setMax((m) => !m)}
                      aria-label={max ? 'Restore' : 'Maximize'} title={max ? 'Restore' : 'Maximize'}>
                <i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} aria-hidden="true" />
              </button>
            )}
            <button className="ad-close" onClick={onClose} aria-label="Close" title="Close">
              ✕
            </button>
          </span>
        </div>
        <div className="ad-body">{children}</div>
        {footer && <div className="ad-ftr">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
